// deps
import * as React from "react";

// internal-deps
import {
    CheckoutRoute,
    CheckoutContext,
    CheckoutActionTypes,
    useNavigate,
    HardwareBackContext,
    recordNREvent,
    NEWRELIC_TABLES,
} from "@swiggy-private/checkout-core";
import { RouteContext } from "@swiggy-private/core-ui";
import { isAndroidPWA } from "@payment-client/helpers/Misc";

export function RouteContainer(props: { children: React.ReactElement | null }): React.ReactElement | null {
    const { checkoutState } = React.useContext(CheckoutContext);
    const { setState: setHardwareBackState } = React.useContext(HardwareBackContext);
    const navigateTo = useNavigate();
    const { route } = React.useContext(RouteContext);
    const type = checkoutState.type;

    // TODO: Move to hooks
    const handleBack = React.useCallback(
        (event: PopStateEvent): void => {
            // handle scenario when user is on timer page
            if (route?.name === CheckoutRoute.VPA_TIMER) {
                history.pushState({ name: CheckoutRoute.VPA_TIMER, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route.name,
                });
                return;
            }

            // handle scenario when user is on add-swiggy-monet page and want to go back
            else if (route?.name === CheckoutRoute.ADD_SWIGGY_MONEY) {
                history.pushState({ name: CheckoutRoute.ADD_SWIGGY_MONEY, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route.name,
                });
                return;
            }

            // handle scenario when user is on home page and want to go back
            else if (route?.name === CheckoutRoute.PAYMENTS) {
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route.name,
                });
                return;
            }

            // handle scenario when user is on swiggy payments page and want to go back
            else if (route?.name === CheckoutRoute.SWIGGY_MONEY_PAYMENTS) {
                history.pushState({ name: CheckoutRoute.SWIGGY_MONEY_PAYMENTS, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route.name,
                });
                return;
            }

            // handle scenario when user is on payment txn status poller page and want to go back
            else if (route?.name === CheckoutRoute.PAYMENT_TXN_STATUS_POLLING) {
                history.pushState({ name: CheckoutRoute.PAYMENT_TXN_STATUS_POLLING, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            }

            // handle scenario when user on confirm order state do not navigate
            else if (
                route?.name === CheckoutRoute.CONFIRM_ORDER ||
                route?.name === CheckoutRoute.DIRECT_PLACE_ORDER ||
                route?.name === CheckoutRoute.TURBO_PLACE_ORDER
            ) {
                history.pushState({ name: route.name, data: route?.data }, "", "");
                return;
            }
            // handle scenario when user has linked the wallet and want to go back
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            else if (route?.name === CheckoutRoute.VERIFY_WALLET_LINK_OTP && !!(route?.data as any)?.stopNavigation) {
                history.pushState({ name: route?.name, data: route?.data }, "", "");
                return;
            }

            // On Android PWA, external payment redirects (e.g. AmazonPay) are intercepted by
            // Android App Links which opens a CCT while the SPA stays alive. The browser fires a
            // spurious popstate that would otherwise remount ShowPayments and trigger a premature
            // confirm-order. Block this popstate by re-pushing the current state.
            else if (route?.name === CheckoutRoute.REDIRECT_PAGE && isAndroidPWA()) {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.PWA_REDIRECT_POPSTATE_DEBUG,
                    popstateTargetRoute: event.state?.name || "--",
                    checkoutStateType: String(type),
                    routeData: JSON.stringify(route?.data ?? {}),
                    historyLength: String(window.history.length),
                    referrer: document.referrer || "--",
                });
                history.pushState({ name: route?.name, data: route?.data }, "", "");
                return;
            }

            if (event.state && event.state.name) {
                navigateTo(event.state.name, event.state.data);
            }
        },
        [route, navigateTo, setHardwareBackState, type],
    );

    React.useEffect(() => {
        window.addEventListener("popstate", handleBack);

        return (): void => {
            window.removeEventListener("popstate", handleBack);
        };
    }, [handleBack]);

    React.useEffect(() => {
        if (type === CheckoutActionTypes.SHOW_PAYMENTS) {
            if (
                [
                    CheckoutRoute.PAYMENTS,
                    CheckoutRoute.ADD_NEW_CARD,
                    CheckoutRoute.LINK_PAYLATER,
                    CheckoutRoute.LINK_PAYLATER_MOBILE,
                    CheckoutRoute.VERIFY_WALLET_LINK_OTP,
                    CheckoutRoute.LINK_WALLET_WITH_NUMBER,
                    CheckoutRoute.PAYMENTS_INDEX,
                    CheckoutRoute.MORE_BANKS,
                    CheckoutRoute.VERIFY_VPA,
                    CheckoutRoute.PAYMENT_TXN_STATUS_POLLING,
                    CheckoutRoute.MORE_PAYMENT_GROUPS,
                    CheckoutRoute.MORE_PAYMENT_METHODS,
                    CheckoutRoute.PAYLATER_LINKING_CONFIRMATION,
                    CheckoutRoute.PAYMENT_OFFERS,
                    CheckoutRoute.EMI_SELECTION,
                    CheckoutRoute.REVIEW_ORDER_DETAILS,
                ].indexOf(route?.name as CheckoutRoute) === -1
            ) {
                navigateTo(CheckoutRoute.PAYMENTS);
                return;
            }
        }

        if (type == CheckoutActionTypes.SHOW_SWIGGYMONEY_PAYMENTS) {
            if (
                [
                    CheckoutRoute.SWIGGY_MONEY_PAYMENTS,
                    CheckoutRoute.ADD_SWIGGY_MONEY,
                    CheckoutRoute.ADD_NEW_CARD,
                    CheckoutRoute.PAYMENT_TXN_STATUS_POLLING,
                    CheckoutRoute.MORE_BANKS,
                    CheckoutRoute.MORE_PAYMENT_GROUPS,
                    CheckoutRoute.MORE_PAYMENT_METHODS,
                ].indexOf(route?.name as CheckoutRoute) === -1
            ) {
                navigateTo(CheckoutRoute.SWIGGY_MONEY_PAYMENTS);
            }
            return;
        }

        /** if Checkout state is webview refresh mismatch show payments **/
        if (type === CheckoutActionTypes.WEBVIEW_REFRESH_ORDER_ID_MISMATCH_ALERT) {
            route?.name !== CheckoutRoute.PAYMENTS && navigateTo(CheckoutRoute.PAYMENTS);
            return;
        }

        if (type === CheckoutActionTypes.SHOW_FULL_PAGE_LOADER) {
            route?.name !== CheckoutRoute.REDIRECT_PAGE && navigateTo(CheckoutRoute.REDIRECT_PAGE);
            return;
        }

        if (type === CheckoutActionTypes.POST_SWIGGY_ADD_MONEY || type === CheckoutActionTypes.POST_PAYLATER_LINKING) {
            route?.name !== CheckoutRoute.PAYMENTS && navigateTo(CheckoutRoute.PAYMENTS);
        }

        if (type === CheckoutActionTypes.DINEOUT_PAYMENT_SUCCESS) {
            if (route?.name !== CheckoutRoute.DINEOUT_PAYMENT_SUCCESS) {
                navigateTo(CheckoutRoute.DINEOUT_PAYMENT_SUCCESS);
            }
        }

        if (type === CheckoutActionTypes.DINEOUT_DIRECT_PLACE_ORDER) {
            route?.name !== CheckoutRoute.DINEOUT_DIRECT_PLACE_ORDER &&
                navigateTo(CheckoutRoute.DINEOUT_DIRECT_PLACE_ORDER);
        }

        /** if Checkout state if vpa timer */
        if (type === CheckoutActionTypes.VPA_TIMER) {
            route?.name !== CheckoutRoute.VPA_TIMER && navigateTo(CheckoutRoute.VPA_TIMER);
            return;
        }

        /** if Checkout state if upi intent poller */
        if (type === CheckoutActionTypes.PAYMENT_TXN_STATUS_POLLER) {
            route?.name !== CheckoutRoute.PAYMENT_TXN_STATUS_POLLING &&
                navigateTo(CheckoutRoute.PAYMENT_TXN_STATUS_POLLING);
            return;
        }

        /** if Checkout state if confirm order */
        if (type === CheckoutActionTypes.CONFIRM_ORDER) {
            route?.name !== CheckoutRoute.CONFIRM_ORDER && navigateTo(CheckoutRoute.CONFIRM_ORDER);
        }

        /** if Checkout state if direct place order */
        if (type === CheckoutActionTypes.DIRECT_PLACE_ORDER) {
            route?.name !== CheckoutRoute.DIRECT_PLACE_ORDER && navigateTo(CheckoutRoute.DIRECT_PLACE_ORDER);
        }

        // For Autopay Phase 2, when payment fails both failure and success(tick screen)
        // are handled by same component.
        // Also we dont show tick screen in these cases.
        // This navigation is INTENTIONAL.
        if (
            type === CheckoutActionTypes.AUTOPAY_ELIGIBLE_PAYMENT_FAILED ||
            type === CheckoutActionTypes.AUTOPAY_ORDER_PROCESS_SUCCESSFUL
        ) {
            route?.name !== CheckoutRoute.AUTOPAY_ELIGIBLE_PAYMENT_FAILED &&
                navigateTo(CheckoutRoute.AUTOPAY_ELIGIBLE_PAYMENT_FAILED);
        }
    }, [type, navigateTo, route]);

    return props.children;
}
